import React, { useState, useEffect } from 'react';
import { Input, List, Avatar } from 'antd';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import axios from 'axios';

const { Search } = Input;

const DrawerContainer = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.visible ? '0' : '-320px'};
  width: 320px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transition: right 0.3s ease;
  z-index: 999;
  display: flex;
  flex-direction: column;
`;

const DrawerHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
  font-weight: 500;
`;

const SearchContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const StyledList = styled(List)`
  flex: 1;
  overflow-y: auto;
  
  .ant-list-item {
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: #f5f5f5;
    }
  }
`;

const ChatList = StyledList as typeof StyledList & { Item: typeof List.Item };

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const ChatInfo = styled.div`
  flex: 1;
  margin-left: 12px;
  overflow: hidden;
`;

const ChatName = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LastMessage = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatTime = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  text-align: right;
  margin-bottom: 4px;
`;

const UnreadBadge = styled.div`
  background: #ff4d4f;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimeAndBadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 60px;
`;

interface UserInfo {
  id: number;
  name: string;
  avatar: string;
}

interface Conversation {
  _id: string;
  userId: number;
  sellerId: number;
  userInfo: UserInfo;
  sellerInfo: UserInfo;
  createdAt: number;
  updatedAt: number;
  status: string;
}

interface ChatDrawerProps {
  visible: boolean;
  onClose: () => void;
  onChatSelect: (conversationId: string, userInfo: UserInfo, conversation: Conversation) => void;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
  visible,
  onClose,
  onChatSelect,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchConversations = async (pageNumber: number) => {
    try {
      setLoading(true);
      const params = {
        skip: (pageNumber - 1) * limit,
        limit
      };

      const response = await axios.get('/admin/chat/get-list-conversation', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.code === 0) {
        const { data, total: totalItems } = response.data.data;
        if (pageNumber === 1) {
          setConversations(data);
        } else {
          setConversations(prev => [...prev, ...data]);
        }
        setTotal(totalItems);
        setHasMore(conversations.length < totalItems);
      }
    } catch (error) {
      console.error('Lỗi khi lấy danh sách hội thoại:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setPage(1);
      setConversations([]);
      fetchConversations(1);
    }
  }, [visible]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) { // Load more khi còn 50% cuối danh sách
      if (!loading && conversations.length < total) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchConversations(nextPage);
      }
    }
  };

  return (
    <DrawerContainer visible={visible}>
      <DrawerHeader>Tin nhắn</DrawerHeader>
      
      <SearchContainer>
        <Search
          placeholder="Tìm kiếm theo tên"
          allowClear
        />
      </SearchContainer>

      <ChatList
        loading={loading}
        dataSource={conversations}
        onScroll={handleScroll}
        renderItem={(conversation: Conversation) => (
          <List.Item onClick={() => onChatSelect(conversation._id, conversation.userInfo, conversation)}>
            <ChatItem>
              <Avatar src={conversation.userInfo.avatar}>
                {conversation.userInfo.name[0]}
              </Avatar>
              <ChatInfo>
                <ChatName>
                  {conversation.userInfo.name}
                </ChatName>
                <LastMessage>
                  {/* Có thể thêm tin nhắn cuối cùng nếu API cung cấp */}
                </LastMessage>
              </ChatInfo>
              <TimeAndBadgeContainer>
                <ChatTime>
                  {formatDistanceToNow(new Date(conversation.updatedAt), { 
                    addSuffix: true,
                    locale: vi 
                  })}
                </ChatTime>
              </TimeAndBadgeContainer>
            </ChatItem>
          </List.Item>
        )}
      />
    </DrawerContainer>
  );
};

export default ChatDrawer; 